import { useState, useEffect } from 'react';
import { X, FileText, Upload, BookOpen, Info, Search, Plus, Layers, FileEdit, Crown, ChevronDown, ChevronRight, Bookmark, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import { useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { documentApi, userApi, draftApi, patentApi, collectionsApi } from '@/services/api';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';
import { splitDocumentEntries, cleanDocumentNumber, validateDocumentNumbers } from '@/utils/documentNumbers';
import { usePremium } from '@/hooks/usePremium';

interface DocumentOnboardingProps {
  onClose: () => void;
  contextData?: any;
  sessionApplications?: any[];
  onDocumentAdded?: () => void;
  onOpenUploadModal?: () => void;
  onSwitchToBatch?: () => void;
}

const RESOURCE_TYPES = [
  { id: 'patent', label: 'US Patent', icon: FileText, description: 'Public US Patent Number' },
  { id: 'publication', label: 'US Publication', icon: FileText, description: 'Public US Publication Number' },
  { id: 'application', label: 'US Application', icon: FileText, description: 'Public US Application Number' },
  { id: 'foreign_patent', label: 'Foreign Patent', icon: FileText, description: 'Foreign Patent Number' },
  { id: 'prosecution_document', label: 'Prosecution Document', icon: FileText, description: 'Office actions, notices, and other prosecution documents' },
  { id: 'collection', label: 'From Collection', icon: Bookmark, description: 'Add documents from your collections' },
  { id: 'draft', label: 'Draft Document', icon: FileEdit, description: 'Add a draft to session documents' },
  { id: 'upload', label: 'Upload Document', icon: Upload, description: 'Upload your own document' },
  { id: 'mpep', label: 'MPEP', icon: BookOpen, description: 'Manual of Patent Examining Procedure' },
  { id: 'cfr', label: '37 CFR', icon: Info, description: 'Code of Federal Regulations' },
  { id: 'usc', label: '35 USC', icon: Info, description: 'United States Code' },
];

// Types that show your onboarded library first, with a search box over it.
const LIBRARY_TYPES = ['patent', 'publication', 'application', 'draft'];
// Types that can be onboarded from a number typed by hand.
const NUMBERED_TYPES = ['patent', 'publication', 'application', 'foreign_patent'];

export function DocumentOnboarding({
  onClose,
  contextData,
  sessionApplications = [],
  onDocumentAdded,
  onOpenUploadModal,
  onSwitchToBatch,
}: DocumentOnboardingProps) {
  const [selectedType, setSelectedType] = useState<string>('');
  const [documentIds, setDocumentIds] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [userDocuments, setUserDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();
  const { isPaid, isPremium } = usePremium();
  const [showActiveDocsInfoModal, setShowActiveDocsInfoModal] = useState(false);
  const [usageData, setUsageData] = useState<any>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [prosecutionDocuments, setProsecutionDocuments] = useState<any[]>([]);
  const [loadingProsecutionDocs, setLoadingProsecutionDocs] = useState(false);

  // Collections browsing state
  const [collectionsList, setCollectionsList] = useState<Array<{ collection_id: string; name: string; created_date: string; resource_ids: string[] }>>([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
  const [selectedCollectionDocs, setSelectedCollectionDocs] = useState<Set<string>>(new Set());
  const [addingCollectionDocs, setAddingCollectionDocs] = useState(false);

  // Fetch user documents when patent, publication, application, or draft is selected
  useEffect(() => {
    const fetchUserDocuments = async () => {
      if (selectedType === 'patent' || selectedType === 'publication') {
        setLoadingDocuments(true);
        try {
          const documents = await documentApi.fetchSessionDocuments(100);
          // Filter by the selected type using file_type field
          const filtered = documents.filter(doc =>
            (doc.file_type || '').toLowerCase() === selectedType.toLowerCase()
          );
          setUserDocuments(filtered);
        } catch (error) {
          console.error('Failed to load user documents:', error);
          setUserDocuments([]);
        } finally {
          setLoadingDocuments(false);
        }
      } else if (selectedType === 'application' || selectedType === 'prosecution_document') {
        setLoadingDocuments(true);
        try {
          // Build application documents from sessionApplications
          const appDocs = (sessionApplications || [])
            .filter((sessionApp: any) => {
              const appNumber = sessionApp.application_id || sessionApp.application?.applicationNumberText;
              const hasValidData = sessionApp.application &&
                sessionApp.application.applicationMetaData &&
                sessionApp.application.applicationMetaData.inventionTitle &&
                sessionApp.application.applicationMetaData.inventionTitle !== 'null';
              return appNumber && hasValidData;
            })
            .map((sessionApp: any) => {
              const appNumber = sessionApp.application_id || sessionApp.application?.applicationNumberText;
              const metaData = sessionApp.application?.applicationMetaData;
              return {
                id: `app-${appNumber}`,
                resource_id: `application-${appNumber}`,
                title: metaData?.inventionTitle || `Application ${appNumber}`,
                file_type: selectedType,
                applicationMetaData: metaData,
                fullApplication: sessionApp.application // Store full application data for prosecution docs
              };
            });
          setUserDocuments(appDocs);
        } catch (error) {
          console.error('Failed to load applications:', error);
          setUserDocuments([]);
        } finally {
          setLoadingDocuments(false);
        }
      } else if (selectedType === 'draft') {
        setLoadingDocuments(true);
        try {
          // Load drafts from API
          const response = await draftApi.listDrafts();
          const allDrafts = response.drafts || [];

          // Convert drafts to document format
          const draftDocs = allDrafts.map((draft: any) => ({
            id: draft.draft_id,
            resource_id: draft.draft_id,
            title: typeof draft.title === 'string' ? draft.title : (draft.title?.title || 'Untitled Draft'),
            file_type: 'draft',
            draft_type: draft.draft_type
          }));

          setUserDocuments(draftDocs);
        } catch (error) {
          console.error('Failed to load drafts:', error);
          setUserDocuments([]);
        } finally {
          setLoadingDocuments(false);
        }
      } else {
        setUserDocuments([]);
      }
    };

    fetchUserDocuments();
  }, [selectedType, sessionApplications]);

  // Load collections when "collection" type is selected
  useEffect(() => {
    if (selectedType === 'collection') {
      setLoadingCollections(true);
      collectionsApi.list()
        .then(setCollectionsList)
        .catch(() => setCollectionsList([]))
        .finally(() => setLoadingCollections(false));
      setSelectedCollectionDocs(new Set());
      setExpandedCollections(new Set());
    }
  }, [selectedType]);

  // Listen for job completion events
  // Note: Notifications are handled globally in Main.tsx, this only handles cleanup
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[DocumentOnboarding] Job completion event received:', event.detail);

      // Format type name properly (handle foreign_patent -> Foreign Patent)
      const resourceTypeName = docType === 'foreign_patent'
        ? 'Foreign Patent'
        : docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        removeLoadingDocument(docId, resourceTypeName);

        if (onDocumentAdded) {
          onDocumentAdded();
        }
      } else {
        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'patent') {
          context.current_patents = (context.current_patents || []).filter((id: string) => id !== docId);
        } else if (docType === 'publication') {
          context.current_publications = (context.current_publications || []).filter((id: string) => id !== docId);
        } else if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        } else if (docType === 'foreign_patent') {
          context.current_foreign_patents = (context.current_foreign_patents || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [onDocumentAdded]);

  // Fetch usage data on mount to show remaining quota
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const response = await userApi.fetchUsage();
        if (response) {
          setUsageData(response);
        }
      } catch (error) {
        console.error('Failed to fetch usage data:', error);
      } finally {
        setLoadingUsage(false);
      }
    };

    fetchUsageData();
  }, []);

  const validateNumbers = (input: string, resourceType: string) => {
    setValidationErrors(validateDocumentNumbers(input, resourceType));
  };

  const handleSubmit = async (overrideDocumentId?: string) => {
    if (!selectedType) {
      addNotification({
        type: 'error',
        title: 'No Resource Type Selected',
        message: 'Please select a resource type first',
      });
      return;
    }

    // Use override if provided, otherwise use state
    const idsToProcess = overrideDocumentId || documentIds;

    // Ensure idsToProcess is a string
    if (typeof idsToProcess !== 'string') {
      console.error('Invalid idsToProcess:', idsToProcess);
      return;
    }

    const entries = splitDocumentEntries(idsToProcess);

    if (entries.length === 0) {
      addNotification({
        type: 'error',
        title: 'No Documents Entered',
        message: `Please enter at least one ${selectedType} number`,
      });
      return;
    }

    // Only check validation errors if not using override (manual entry)
    if (!overrideDocumentId && validationErrors.length > 0) {
      addNotification({
        type: 'error',
        title: 'Validation Errors',
        message: 'Please fix the validation errors before submitting',
      });
      return;
    }

    // Check if user is trying to onboard foreign patents without a paid plan
    if (selectedType === 'foreign_patent' && !isPaid) {
      addNotification({
        type: 'warning',
        title: 'Pro or Premium Required',
        message: 'Foreign patent onboarding is available for Pro and Premium subscribers. Please upgrade to access this feature.',
        duration: 6000,
      });
      return;
    }

    // Check daily quota for patent/publication/application/foreign_patent types
    if (['patent', 'publication', 'application', 'foreign_patent'].includes(selectedType) && usageData?.daily_onboarding) {
      const remaining = usageData.daily_onboarding.patent_publications.remaining;
      if (remaining <= 0) {
        addNotification({
          type: 'error',
          title: 'Daily Limit Reached',
          message: `You have reached your daily limit for patent/publication onboarding (${usageData.daily_onboarding.patent_publications.limit} per day). The limit will reset tomorrow.`,
          duration: 8000,
        });
        return;
      }
      if (entries.length > remaining) {
        addNotification({
          type: 'warning',
          title: 'Approaching Daily Limit',
          message: `You are trying to add ${entries.length} documents but only have ${remaining} remaining today. Please reduce the number of documents or try again tomorrow.`,
          duration: 8000,
        });
        return;
      }
    }

    // Clean and process entries
    const cleanedIds = entries.map((id) => cleanDocumentNumber(id, selectedType));

    setIsSubmitting(true);

    // Add documents to session context immediately
    const stored = localStorage.getItem('session_context');
    const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

    // Track if there was a quota error (to prevent auto-closing modal)
    let quotaErrorOccurred = false;

    // Handle different resource types
    if (['patent', 'publication', 'application', 'foreign_patent'].includes(selectedType)) {
      // For patent/publication/application/foreign_patent, call fetchAndLoadDocument for each
      for (const cleanedId of cleanedIds) {
        // Check if already active — skip duplicates
        const contextKey = selectedType === 'patent' ? 'current_patents'
          : selectedType === 'publication' ? 'current_publications'
          : selectedType === 'application' ? 'current_applications'
          : 'current_foreign_patents';
        if ((context[contextKey] || []).includes(cleanedId)) {
          addNotification({
            type: 'info',
            title: 'Already Active',
            message: `${cleanedId} is already in your active session.`,
            duration: 3000,
          });
          continue;
        }

        try {
          const response = await documentApi.fetchAndLoadDocument(selectedType, cleanedId);

          // Check if response is an error (backend returns errors with 200 status)
          const responseData = Array.isArray(response) ? response[0] : response;

          if (responseData?.error === 'daily_limit_exceeded') {
            quotaErrorOccurred = true;
            const errorData = responseData;

            // Show notification with longer duration and add to notification center
            addNotification({
              type: 'error',
              title: 'Daily Limit Reached',
              message: `${errorData.message} You have used ${errorData.current}/${errorData.limit} of your daily allowance. The limit will reset tomorrow.`,
              duration: 20000,
              persistent: true,
            });

            // Refresh usage data
            try {
              const usageResponse = await userApi.fetchUsage();
              if (usageResponse) {
                setUsageData(usageResponse);
              }
            } catch (e) {
              console.error('Failed to refresh usage data:', e);
            }

            // Close the onboarding window
            setIsSubmitting(false);
            onClose();
            return;
          }

          if (response?.job_id) {
            // Add to session context
            if (selectedType === 'patent') {
              context.current_patents = [...(context.current_patents || []), cleanedId];
            } else if (selectedType === 'publication') {
              context.current_publications = [...(context.current_publications || []), cleanedId];
            } else if (selectedType === 'application') {
              context.current_applications = [...(context.current_applications || []), cleanedId];
            } else if (selectedType === 'foreign_patent') {
              context.current_foreign_patents = [...(context.current_foreign_patents || []), cleanedId];
            }

            // Update context immediately so it shows in UI
            localStorage.setItem('session_context', JSON.stringify(context));
            window.dispatchEvent(new Event('storage-update'));

            // Show processing notification
            // Format type name properly (handle foreign_patent -> Foreign Patent)
            const resourceTypeName = selectedType === 'foreign_patent'
              ? 'Foreign Patent'
              : selectedType.charAt(0).toUpperCase() + selectedType.slice(1);
            addNotification({
              type: 'info',
              title: `Loading ${resourceTypeName}`,
              message: `Processing ${cleanedId}...`,
              duration: 60000, // Long duration since we'll update it
            });

            // Add to loading documents tracking
            addLoadingDocument(cleanedId, resourceTypeName, cleanedId);

            // Start polling for job status
            pollJobStatus(response.job_id, selectedType, cleanedId);
          }
        } catch (error: any) {
          console.error(`Failed to submit ${selectedType} ${cleanedId}:`, error);
          console.log('Error response:', error?.response);
          console.log('Error response data:', error?.response?.data);

          // Extract error data - Flask returns [data, status_code] as array
          const responseData = Array.isArray(error?.response?.data)
            ? error.response.data[0]
            : error?.response?.data;

          console.log('Extracted error data:', responseData);

          // Check if it's a quota exceeded error
          if (responseData?.error === 'daily_limit_exceeded') {
            console.log('QUOTA EXCEEDED ERROR DETECTED');
            quotaErrorOccurred = true; // Set flag to prevent modal from closing
            const errorData = responseData;

            // Show notification
            addNotification({
              type: 'error',
              title: 'Daily Limit Reached',
              message: `${errorData.message} You have used ${errorData.current}/${errorData.limit} of your daily allowance.`,
              duration: 8000,
            });

            console.log('Notification shown, refreshing usage data...');

            // Refresh usage data
            try {
              const response = await userApi.fetchUsage();
              if (response) {
                setUsageData(response);
                console.log('Usage data refreshed:', response);
              }
            } catch (e) {
              console.error('Failed to refresh usage data:', e);
            }

            setIsSubmitting(false); // Re-enable submit button
            console.log('Returning early to keep modal open');
            return; // Exit early, don't close modal
          } else {
            console.log('Other error, showing generic notification');
            addNotification({
              type: 'error',
              title: 'Submission Failed',
              message: `Failed to submit ${selectedType} ${cleanedId}. Please try again.`,
              duration: 5000,
            });
            setIsSubmitting(false);
            return; // Also return on other errors to keep modal open
          }
        }
      }
    } else {
      // For other types (mpep, cfr, usc, etc.), add directly to context
      if (selectedType === 'mpep') {
        context.mpep_sections = [...(context.mpep_sections || []), ...cleanedIds];
      } else if (selectedType === 'cfr') {
        context.cfr_sections = [...(context.cfr_sections || []), ...cleanedIds];
      } else if (selectedType === 'usc') {
        context.usc_sections = [...(context.usc_sections || []), ...cleanedIds];
      }

      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Documents Added',
        message: `Added ${cleanedIds.length} ${selectedType} section(s) to session.`,
        duration: 5000,
      });
    }

    setIsSubmitting(false);

    if (onDocumentAdded) {
      onDocumentAdded();
    }

    // Reset and close
    setDocumentIds('');
    setDocumentSearchQuery('');
    setSelectedType('');
    setValidationErrors([]);
    onClose();
  };

  /**
   * What the footer's Add button does, from whichever mode is on screen.
   *
   * In list mode the search box does double duty: it filters the documents already
   * onboarded, and whatever is in it is also the number to onboard when nothing in the
   * list was picked. A number not in your library is exactly the case where you typed it
   * out in full — being told to click "Enter Manually" and type it a second time is the
   * step this removes.
   */
  const handleAdd = async () => {
    const inListMode = LIBRARY_TYPES.includes(selectedType) && !showManualEntry;
    if (!inListMode) {
      await handleSubmit();
      return;
    }

    const typed = documentSearchQuery.trim();

    // A draft is a document in your library or nothing — there is no number to onboard.
    if (selectedType === 'draft') {
      addNotification({
        type: 'error',
        title: 'No Draft Selected',
        message: 'Pick a draft from the list to add it.',
      });
      return;
    }

    if (!typed) {
      addNotification({
        type: 'error',
        title: 'Nothing to Add',
        message: `Pick one from the list, or type a ${selectedType} number to onboard it.`,
      });
      return;
    }

    // Checked here rather than while typing: until Add is pressed the box is a filter,
    // and half a number is a perfectly good filter.
    const errors = NUMBERED_TYPES.includes(selectedType)
      ? validateDocumentNumbers(typed, selectedType)
      : [];
    setValidationErrors(errors);
    if (errors.length > 0) {
      addNotification({
        type: 'error',
        title: 'Check the Number',
        message: errors[0],
      });
      return;
    }

    await handleSubmit(typed);
  };

  const pollJobStatus = (jobId: string, docType: string, docId: string) => {
    console.log('[DocumentOnboarding] Starting poll via global store:', { jobId, docType, docId });
    startPolling(jobId, docType, docId);
  };

  const handleAddDraft = async (draftId: string) => {
    try {
      // Get the full draft from API
      const response = await draftApi.listDrafts();
      const allDrafts = response.drafts || [];
      const draftData = allDrafts.find((d: any) => d.draft_id === draftId);

      if (!draftData) {
        addNotification({
          type: 'error',
          title: 'Draft Not Found',
          message: 'Could not find the selected draft',
        });
        return;
      }

      // Normalize the draft data
      const draft = {
        draft_id: draftData.draft_id,
        title: typeof draftData.title === 'string' ? draftData.title : (draftData.title?.title || 'Untitled Draft'),
        content: typeof draftData.content === 'string' ? draftData.content : (draftData.content?.content || ''),
        draft_type: draftData.draft_type
      };

      // Add to active_drafts
      const activeDraftsStr = localStorage.getItem('active_drafts');
      const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];

      // Check if already active
      if (activeDrafts.some((d: any) => d.draft_id === draftId)) {
        addNotification({
          type: 'info',
          title: 'Already Active',
          message: 'This draft is already in session documents',
        });
        return;
      }

      // Add draft
      activeDrafts.push(draft);
      localStorage.setItem('active_drafts', JSON.stringify(activeDrafts));

      // Also update session context
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
      context.current_drafts = activeDrafts;
      localStorage.setItem('session_context', JSON.stringify(context));

      // Dispatch event to update UI
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Draft Added',
        message: `"${draft.title || 'Untitled Draft'}" added to session documents`,
      });

      if (onDocumentAdded) {
        onDocumentAdded();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to add draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to add draft to session documents',
      });
    }
  };

  const handleSelectApplicationForProsecution = async (application: any) => {
    setSelectedApplication(application);
    setLoadingProsecutionDocs(true);

    try {
      const appNumber = application.resource_id.replace('application-', '');

      // Fetch prosecution documents from the API (same as USPTO Data page does)
      const result = await patentApi.fetchProsecutionDocInfo(appNumber);

      const prosDocs = result?.pros_info
        ?.filter((doc: any) => doc.documentIdentifier)
        .map((doc: any) => ({
          documentIdentifier: doc.documentIdentifier,
          documentDescription: doc.documentCodeDescriptionText || doc.documentCode,
          mailDate: doc.mailDate ? new Date(doc.mailDate).toLocaleDateString() : null,
          documentCode: doc.documentCode,
          officialDate: doc.officialDate ? new Date(doc.officialDate).toLocaleDateString() : 'N/A',
        })) || [];

      if (prosDocs.length > 0) {
        // Sort by date (most recent first)
        prosDocs.sort((a: any, b: any) => {
          if (!a.officialDate || a.officialDate === 'N/A') return 1;
          if (!b.officialDate || b.officialDate === 'N/A') return -1;
          return new Date(b.officialDate).getTime() - new Date(a.officialDate).getTime();
        });

        setProsecutionDocuments(prosDocs);
      } else {
        setProsecutionDocuments([]);
        addNotification({
          type: 'info',
          title: 'No Prosecution Documents',
          message: 'No prosecution documents found for this application',
        });
      }
    } catch (error) {
      console.error('Failed to load prosecution documents:', error);
      setProsecutionDocuments([]);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load prosecution documents',
      });
    } finally {
      setLoadingProsecutionDocs(false);
    }
  };

  const handleAddProsecutionDoc = async (prosDoc: any) => {
    if (!selectedApplication) return;

    try {
      const appNumber = selectedApplication.resource_id.replace('application-', '');
      const description = prosDoc.documentDescription || prosDoc.documentCode;

      // Add prosecution document to session using the API (same as USPTO Data page does)
      // Note: API expects just the documentIdentifier, not the combined appNumber-docId format
      await patentApi.addProsecutionDocToSession(appNumber, prosDoc.documentIdentifier, description);

      addNotification({
        type: 'success',
        title: 'Prosecution Document Added',
        message: `"${description}" added to session documents`,
      });

      if (onDocumentAdded) {
        onDocumentAdded();
      }

      // Trigger storage update event to refresh session state
      window.dispatchEvent(new Event('storage-update'));

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to add prosecution document:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to add prosecution document to session documents',
      });
    }
  };

  // Calculate current resources
  const activeDraftsStr = typeof window !== 'undefined' ? localStorage.getItem('active_drafts') : null;
  const activeDraftsCount = activeDraftsStr ? JSON.parse(activeDraftsStr).length : 0;

  const resourceCounts = {
    patents: contextData?.current_patents?.length || 0,
    publications: contextData?.current_publications?.length || 0,
    applications: contextData?.current_applications?.length || 0,
    foreign_patents: contextData?.current_foreign_patents?.length || 0,
    prosecution_docs: contextData?.pros_docs?.length || 0,
    drafts: activeDraftsCount,
    uploads: contextData?.resource_ids?.length || 0,
    mpep: contextData?.mpep_sections?.length || 0,
    cfr: contextData?.cfr_sections?.length || 0,
    usc: contextData?.usc_sections?.length || 0,
  };

  const totalCount = Object.values(resourceCounts).reduce((a, b) => a + b, 0);

  // ── Collection helpers ──────────────────────────────────────────────────────

  const formatCollectionResourceId = (resourceId: string): string => {
    if (!resourceId) return resourceId;
    if (resourceId.startsWith('patent-')) {
      const num = resourceId.replace('patent-', '');
      return `US Patent ${Number(num).toLocaleString('en-US')}`;
    }
    if (resourceId.startsWith('publication-')) {
      return `US Pub ${resourceId.replace('publication-', '')}`;
    }
    if (resourceId.startsWith('application-')) {
      return `Application ${resourceId.replace('application-', '')}`;
    }
    if (resourceId.startsWith('foreign_patent-')) {
      return resourceId.replace('foreign_patent-', '');
    }
    return resourceId;
  };

  /** Determine the doc type prefix from a resource_id (e.g. "patent-12345" → "patent"). */
  const getDocTypeFromResourceId = (rid: string): string | null => {
    if (rid.startsWith('patent-')) return 'patent';
    if (rid.startsWith('publication-')) return 'publication';
    if (rid.startsWith('application-')) return 'application';
    if (rid.startsWith('foreign_patent-')) return 'foreign_patent';
    // Uploads have opaque resource_ids — treat as upload
    return 'upload';
  };

  /** Check if a resource_id is already active in the session. */
  const isResourceIdActive = (rid: string): boolean => {
    const docType = getDocTypeFromResourceId(rid);
    const cleanedId = rid.replace(/^(patent-|publication-|application-|foreign_patent-)/, '');
    if (docType === 'patent') return (contextData?.current_patents || []).includes(cleanedId);
    if (docType === 'publication') return (contextData?.current_publications || []).includes(cleanedId);
    if (docType === 'application') return (contextData?.current_applications || []).includes(cleanedId);
    if (docType === 'foreign_patent') return (contextData?.current_foreign_patents || []).includes(cleanedId);
    if (docType === 'upload') {
      return (contextData?.resource_ids || []).includes(rid) ||
             (contextData?.resource_names || []).includes(rid);
    }
    return false;
  };

  const toggleCollectionExpanded = (collectionId: string) => {
    setExpandedCollections(prev => {
      const next = new Set(prev);
      if (next.has(collectionId)) next.delete(collectionId);
      else next.add(collectionId);
      return next;
    });
  };

  const toggleCollectionDocSelected = (rid: string) => {
    setSelectedCollectionDocs(prev => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };

  const toggleSelectAllInCollection = (col: { resource_ids: string[] }) => {
    const selectable = col.resource_ids.filter(rid => !isResourceIdActive(rid));
    const allSelected = selectable.every(rid => selectedCollectionDocs.has(rid));
    setSelectedCollectionDocs(prev => {
      const next = new Set(prev);
      if (allSelected) {
        selectable.forEach(rid => next.delete(rid));
      } else {
        selectable.forEach(rid => next.add(rid));
      }
      return next;
    });
  };

  const handleAddCollectionDocs = async () => {
    if (selectedCollectionDocs.size === 0) return;
    setAddingCollectionDocs(true);

    const stored = localStorage.getItem('session_context');
    const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
    let addedCount = 0;
    let skippedCount = 0;

    for (const rid of selectedCollectionDocs) {
      if (isResourceIdActive(rid)) { skippedCount++; continue; }

      const docType = getDocTypeFromResourceId(rid);
      const cleanedId = rid.replace(/^(patent-|publication-|application-|foreign_patent-)/, '');

      // Also check the local context to catch duplicates added earlier in this loop
      const contextKey = docType === 'patent' ? 'current_patents'
        : docType === 'publication' ? 'current_publications'
        : docType === 'application' ? 'current_applications'
        : docType === 'foreign_patent' ? 'current_foreign_patents'
        : null;

      if (contextKey && (context[contextKey] || []).includes(cleanedId)) { skippedCount++; continue; }
      if (docType === 'upload' && (context.resource_ids || []).includes(rid)) { skippedCount++; continue; }

      if (docType === 'patent' || docType === 'publication' || docType === 'application' || docType === 'foreign_patent') {
        try {
          const response = await documentApi.fetchAndLoadDocument(docType, cleanedId);
          if (response?.job_id) {
            context[contextKey!] = [...(context[contextKey!] || []), cleanedId];
            localStorage.setItem('session_context', JSON.stringify(context));
            window.dispatchEvent(new Event('storage-update'));

            const typeName = docType === 'foreign_patent' ? 'Foreign Patent' : docType.charAt(0).toUpperCase() + docType.slice(1);
            addLoadingDocument(cleanedId, typeName, cleanedId);
            startPolling(response.job_id, docType, cleanedId);
            addedCount++;
          }
        } catch (error) {
          console.error(`Failed to add ${rid}:`, error);
        }
      } else if (docType === 'upload') {
        // For uploads, add resource_id directly to context
        const resourceIds = context.resource_ids || [];
        const resourceNames = context.resource_names || [];
        resourceIds.push(rid);
        resourceNames.push(rid); // name fallback — we only have the ID
        context.resource_ids = resourceIds;
        context.resource_names = resourceNames;
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
        addedCount++;
      }
    }

    if (addedCount > 0) {
      const skippedMsg = skippedCount > 0 ? ` (${skippedCount} already active)` : '';
      addNotification({
        type: 'success',
        title: 'Documents Added',
        message: `Adding ${addedCount} document${addedCount !== 1 ? 's' : ''} from collections...${skippedMsg}`,
        duration: 5000,
      });
      if (onDocumentAdded) onDocumentAdded();
    } else if (skippedCount > 0) {
      addNotification({
        type: 'info',
        title: 'Already Active',
        message: `All ${skippedCount} selected document${skippedCount !== 1 ? 's are' : ' is'} already in your active session.`,
        duration: 4000,
      });
    }

    setAddingCollectionDocs(false);
    setSelectedCollectionDocs(new Set());
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900">
                Add Documents to Session
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowActiveDocsInfoModal(true)}
                className="h-7 w-7 p-0 text-sm font-semibold text-gray-600 border-gray-300 hover:bg-gray-100"
                title="How Session documents work"
              >
                ?
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Select a document type and enter one or more document numbers
            </p>
            {!loadingUsage && usageData?.daily_onboarding && ['patent', 'publication', 'application', 'foreign_patent'].includes(selectedType) && (
              <div className="mt-2 flex items-center gap-2">
                <Badge
                  variant={usageData.daily_onboarding.patent_publications.remaining <= 0 ? 'destructive' : usageData.daily_onboarding.patent_publications.remaining <= 5 ? 'secondary' : 'default'}
                  className="text-xs"
                >
                  {usageData.daily_onboarding.patent_publications.remaining} / {usageData.daily_onboarding.patent_publications.limit} remaining today
                </Badge>
                {usageData.daily_onboarding.patent_publications.remaining <= 5 && usageData.daily_onboarding.patent_publications.remaining > 0 && (
                  <span className="text-xs text-amber-600">Approaching daily limit</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onSwitchToBatch && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSwitchToBatch}
                className="gap-2"
              >
                <Layers className="w-4 h-4" />
                Batch Mode
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close add documents">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Current Resources Summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-900 mb-2">
                Current Session Resources ({totalCount} total)
              </div>
              {totalCount > 0 ? (
                <div className="grid grid-cols-3 gap-2 text-xs text-gray-700">
                  {resourceCounts.patents > 0 && (
                    <div>Patents: {resourceCounts.patents}</div>
                  )}
                  {resourceCounts.publications > 0 && (
                    <div>Publications: {resourceCounts.publications}</div>
                  )}
                  {resourceCounts.applications > 0 && (
                    <div>Applications: {resourceCounts.applications}</div>
                  )}
                  {resourceCounts.foreign_patents > 0 && (
                    <div>Foreign Patents: {resourceCounts.foreign_patents}</div>
                  )}
                  {resourceCounts.prosecution_docs > 0 && (
                    <div>Prosecution Docs: {resourceCounts.prosecution_docs}</div>
                  )}
                  {resourceCounts.drafts > 0 && (
                    <div>Drafts: {resourceCounts.drafts}</div>
                  )}
                  {resourceCounts.uploads > 0 && (
                    <div>Uploads: {resourceCounts.uploads}</div>
                  )}
                  {resourceCounts.mpep > 0 && <div>MPEP: {resourceCounts.mpep}</div>}
                  {resourceCounts.cfr > 0 && <div>CFR: {resourceCounts.cfr}</div>}
                  {resourceCounts.usc > 0 && <div>USC: {resourceCounts.usc}</div>}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  No resources in current session
                </div>
              )}
            </div>


            {/* Collection Browser */}
            {selectedType === 'collection' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-900">
                    Browse Collections
                  </label>
                  {selectedCollectionDocs.size > 0 && (
                    <Badge className="bg-blue-600 text-white text-xs">
                      {selectedCollectionDocs.size} selected
                    </Badge>
                  )}
                </div>

                {loadingCollections ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : collectionsList.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bookmark className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No collections yet</p>
                    <p className="text-xs text-gray-400 mt-1">Create collections from the Documents page or sidebar to organize your documents</p>
                  </div>
                ) : (
                  <ScrollArea className="h-72 border border-gray-200 rounded-lg">
                    <div className="p-2 space-y-1">
                      {collectionsList.map(col => {
                        const isExpanded = expandedCollections.has(col.collection_id);
                        const selectableInCol = col.resource_ids.filter(rid => !isResourceIdActive(rid));
                        const selectedInCol = selectableInCol.filter(rid => selectedCollectionDocs.has(rid));
                        const allInColSelected = selectableInCol.length > 0 && selectedInCol.length === selectableInCol.length;

                        return (
                          <div key={col.collection_id} className="border border-gray-200 rounded-lg">
                            {/* Collection header */}
                            <div
                              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-t-lg"
                              onClick={() => toggleCollectionExpanded(col.collection_id)}
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium flex-1 truncate">{col.name}</span>
                              <Badge variant="secondary" className="text-xs flex-shrink-0">
                                {col.resource_ids.length} doc{col.resource_ids.length !== 1 ? 's' : ''}
                              </Badge>
                              {selectableInCol.length > 0 && (
                                <Checkbox
                                  checked={allInColSelected}
                                  onCheckedChange={() => toggleSelectAllInCollection(col)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                            </div>

                            {/* Expanded doc list */}
                            {isExpanded && (
                              <div className="border-t border-gray-100">
                                {col.resource_ids.length === 0 ? (
                                  <div className="px-4 py-3 text-xs text-gray-400 italic">Empty collection</div>
                                ) : (
                                  col.resource_ids.map(rid => {
                                    const active = isResourceIdActive(rid);
                                    return (
                                      <div
                                        key={rid}
                                        className={`flex items-center gap-2 px-4 py-1.5 ${active ? 'opacity-50' : 'hover:bg-gray-50 cursor-pointer'}`}
                                        onClick={() => { if (!active) toggleCollectionDocSelected(rid); }}
                                      >
                                        {active ? (
                                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">Active</Badge>
                                        ) : (
                                          <Checkbox
                                            checked={selectedCollectionDocs.has(rid)}
                                            onCheckedChange={() => toggleCollectionDocSelected(rid)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        )}
                                        <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 truncate" title={rid}>
                                          {formatCollectionResourceId(rid)}
                                        </span>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}

                {/* Add selected button */}
                {selectedCollectionDocs.size > 0 && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      onClick={handleAddCollectionDocs}
                      disabled={addingCollectionDocs}
                      className="gap-2"
                    >
                      {addingCollectionDocs ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      Add {selectedCollectionDocs.size} Document{selectedCollectionDocs.size !== 1 ? 's' : ''} to Session
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Document List or Manual Entry */}
            {selectedType && selectedType !== 'upload' && selectedType !== 'collection' && (
              <div>
                {/* Special handling for prosecution documents */}
                {selectedType === 'prosecution_document' ? (
                  <div>
                    {!selectedApplication ? (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-900">
                            Select an Application
                          </label>
                        </div>

                        {/* Search */}
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Search your applications..."
                            value={documentSearchQuery}
                            onChange={(e) => setDocumentSearchQuery(e.target.value)}
                            className="pl-9"
                          />
                        </div>

                        {/* Application List */}
                        {loadingDocuments ? (
                          <div className="text-center py-8 text-gray-500">
                            Loading applications...
                          </div>
                        ) : userDocuments.length === 0 ? (
                          <div className="text-center py-8">
                            <p className="text-gray-600 mb-3">
                              No applications found. Please add an application first.
                            </p>
                          </div>
                        ) : (() => {
                          // Filter applications based on search query
                          const filteredDocs = userDocuments.filter(doc => {
                            if (!documentSearchQuery) return true;
                            const searchLower = documentSearchQuery.toLowerCase();
                            const number = doc.resource_id?.toLowerCase() || '';
                            const title = doc.title?.toLowerCase() || '';
                            return number.includes(searchLower) || title.includes(searchLower);
                          });

                          if (filteredDocs.length === 0 && documentSearchQuery) {
                            return (
                              <div className="text-center py-8">
                                <p className="text-gray-600">
                                  No matching applications found for "{documentSearchQuery}"
                                </p>
                              </div>
                            );
                          }

                          return (
                            <ScrollArea className="h-64 border border-gray-200 rounded-lg">
                              <div className="p-2 space-y-2">
                                {filteredDocs.map((doc) => {
                                  const docNumber = doc.resource_id?.replace(/^application-/, '') || '';
                                  return (
                                    <div
                                      key={doc.id}
                                      className="p-3 rounded-lg border bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all"
                                      onClick={() => handleSelectApplicationForProsecution(doc)}
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-sm text-gray-900">
                                            {docNumber}
                                          </div>
                                          {doc.title && (
                                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                              {doc.title}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </ScrollArea>
                          );
                        })()}
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-900">
                            Select Prosecution Document from {selectedApplication.resource_id.replace('application-', '')}
                          </label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedApplication(null);
                              setProsecutionDocuments([]);
                              setDocumentSearchQuery('');
                            }}
                          >
                            ← Back to Applications
                          </Button>
                        </div>

                        {/* Prosecution Documents List */}
                        {loadingProsecutionDocs ? (
                          <div className="text-center py-8 text-gray-500">
                            Loading prosecution documents...
                          </div>
                        ) : prosecutionDocuments.length === 0 ? (
                          <div className="text-center py-8">
                            <p className="text-gray-600">
                              No prosecution documents found for this application.
                            </p>
                          </div>
                        ) : (
                          <ScrollArea className="h-64 border border-gray-200 rounded-lg">
                            <div className="p-2 space-y-2">
                              {prosecutionDocuments.map((prosDoc, idx) => {
                                // Check if already active
                                const appNumber = selectedApplication.resource_id.replace('application-', '');
                                const prosDocId = `${appNumber}-${prosDoc.documentIdentifier}`;
                                const prosDocsInContext = contextData?.pros_docs || [];
                                const isAlreadyActive = prosDocsInContext.includes(prosDocId);

                                return (
                                  <div
                                    key={idx}
                                    className={`p-3 rounded-lg border transition-all ${
                                      isAlreadyActive
                                        ? 'bg-gray-100 border-gray-300 opacity-60'
                                        : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                                    }`}
                                    onClick={() => {
                                      if (!isAlreadyActive) {
                                        handleAddProsecutionDoc(prosDoc);
                                      }
                                    }}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm text-gray-900">
                                          {prosDoc.documentDescription || prosDoc.documentCode}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1">
                                          {prosDoc.mailDate && `Mail Date: ${prosDoc.mailDate}`}
                                          {prosDoc.documentCode && ` • Code: ${prosDoc.documentCode}`}
                                        </div>
                                      </div>
                                      {isAlreadyActive && (
                                        <Badge variant="secondary" className="text-xs ml-2">
                                          Active
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        )}
                      </div>
                    )}
                  </div>
                ) : (selectedType === 'patent' || selectedType === 'publication' || selectedType === 'application' || selectedType === 'draft') && !showManualEntry ? (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-900">
                        Select from Your {selectedType === 'patent' ? 'Patents' : selectedType === 'publication' ? 'Publications' : selectedType === 'application' ? 'Applications' : 'Drafts'}
                      </label>
                      {selectedType !== 'draft' && selectedType !== 'prosecution_document' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowManualEntry(true);
                            if (documentSearchQuery) {
                              setDocumentIds(documentSearchQuery);
                              if (['publication', 'patent', 'application', 'foreign_patent'].includes(selectedType)) {
                                validateNumbers(documentSearchQuery, selectedType);
                              }
                            }
                          }}
                          className="gap-2"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Enter Manually
                        </Button>
                      )}
                    </div>

                    {/* Search — also the box a new document number is typed into */}
                    <div className="mb-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder={
                            selectedType === 'draft'
                              ? 'Search your drafts...'
                              : `Search your ${selectedType === 'application' ? 'applications' : selectedType + 's'}, or type a new number to onboard...`
                          }
                          value={documentSearchQuery}
                          onChange={(e) => {
                            setDocumentSearchQuery(e.target.value);
                            // Errors belong to the last Add attempt; typing again is a
                            // new attempt.
                            if (validationErrors.length > 0) setValidationErrors([]);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isSubmitting) {
                              e.preventDefault();
                              handleAdd();
                            }
                          }}
                          className="pl-9"
                        />
                      </div>
                      {selectedType !== 'draft' && (
                        <div className="mt-1.5 text-xs text-gray-500">
                          {documentSearchQuery.trim()
                            ? `Not in the list? "${documentSearchQuery.trim()}" will be onboarded when you click Add.`
                            : 'Pick one below, or type a number that is not in your library yet and click Add to onboard it.'}
                        </div>
                      )}
                      {validationErrors.length > 0 && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          {validationErrors.map((error, idx) => (
                            <div key={idx}>{error}</div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Document List */}
                    {loadingDocuments ? (
                      <div className="text-center py-8 text-gray-500">
                        Loading your documents...
                      </div>
                    ) : userDocuments.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-600 mb-3">
                          No {selectedType === 'draft' ? 'drafts' : selectedType + 's'} found in your library.
                        </p>
                        {selectedType !== 'draft' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowManualEntry(true)}
                          >
                            Enter Document Number Manually
                          </Button>
                        )}
                      </div>
                    ) : (() => {
                      // Filter documents based on search query
                      const filteredDocs = userDocuments.filter(doc => {
                        if (!documentSearchQuery) return true;
                        const searchLower = documentSearchQuery.toLowerCase();
                        const number = doc.resource_id?.toLowerCase() || '';
                        const title = doc.title?.toLowerCase() || '';
                        return number.includes(searchLower) || title.includes(searchLower);
                      });

                      // Show message if search yielded no results
                      if (filteredDocs.length === 0 && documentSearchQuery) {
                        return (
                          <div className="text-center py-8">
                            <p className="text-gray-600 mb-2">
                              No matching {selectedType === 'draft' ? 'drafts' : selectedType + 's'} found for "{documentSearchQuery}"
                            </p>
                            {selectedType !== 'draft' && (
                              <>
                                <p className="text-sm text-gray-500 mb-3">
                                  It has not been onboarded yet — add it and we will fetch it.
                                </p>
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    disabled={isSubmitting}
                                    onClick={handleAdd}
                                  >
                                    {isSubmitting ? 'Adding...' : `Add "${documentSearchQuery.trim()}"`}
                                  </Button>
                                  {/* Still the way in for several at once, which the one-line
                                      search box is not. */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setDocumentIds(documentSearchQuery);
                                      setShowManualEntry(true);
                                      if (NUMBERED_TYPES.includes(selectedType)) {
                                        validateNumbers(documentSearchQuery, selectedType);
                                      }
                                    }}
                                  >
                                    Add several
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      }

                      return (
                        <ScrollArea className="h-64 border border-gray-200 rounded-lg">
                          <div className="p-2 space-y-2">
                            {filteredDocs.map((doc) => {
                              const docNumber = doc.resource_id?.replace(/^(patent-|publication-|application-)/, '') || '';

                              // Check if already active
                              let isAlreadyActive = false;
                              if (selectedType === 'patent') {
                                isAlreadyActive = contextData?.current_patents?.includes(docNumber);
                              } else if (selectedType === 'publication') {
                                isAlreadyActive = contextData?.current_publications?.includes(docNumber);
                              } else if (selectedType === 'application') {
                                isAlreadyActive = contextData?.current_applications?.includes(docNumber);
                              } else if (selectedType === 'draft') {
                                const activeDraftsStr = localStorage.getItem('active_drafts');
                                const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
                                isAlreadyActive = activeDrafts.some((d: any) => d.draft_id === doc.resource_id);
                              }

                              return (
                                <div
                                  key={doc.id}
                                  className={`p-3 rounded-lg border transition-all ${
                                    isAlreadyActive
                                      ? 'bg-gray-100 border-gray-300 opacity-60'
                                      : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                                  }`}
                                  onClick={() => {
                                    if (!isAlreadyActive) {
                                      if (selectedType === 'draft') {
                                        // Add draft to active documents
                                        handleAddDraft(doc.resource_id);
                                      } else if (docNumber && docNumber.trim()) {
                                        handleSubmit(docNumber);
                                      }
                                    }
                                  }}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-gray-900">
                                        {selectedType === 'draft' ? doc.title : docNumber}
                                      </div>
                                      {selectedType !== 'draft' && doc.title && (
                                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                          {doc.title}
                                        </div>
                                      )}
                                    </div>
                                    {isAlreadyActive && (
                                      <Badge variant="secondary" className="text-xs ml-2">
                                        Active
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>
                      );
                    })()}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-900">
                        Enter Document Numbers
                      </label>
                      {(selectedType === 'patent' || selectedType === 'publication' || selectedType === 'application') && showManualEntry && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowManualEntry(false);
                            setDocumentIds('');
                            setValidationErrors([]);
                          }}
                        >
                          ← Back to List
                        </Button>
                      )}
                    </div>
                    <Textarea
                      rows={4}
                      value={documentIds}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setDocumentIds(newValue);
                        if (['publication', 'patent', 'application', 'foreign_patent'].includes(selectedType)) {
                          validateNumbers(newValue, selectedType);
                        }
                      }}
                      className="resize-none"
                      placeholder={
                        selectedType === 'publication'
                          ? '20240123456\nUS20240123456A1\n2024/0123456'
                          : selectedType === 'patent'
                          ? '12345678\nUS12,345,678\n12/345,678'
                          : selectedType === 'application'
                          ? '12345678\n12/345,678'
                          : selectedType === 'foreign_patent'
                          ? 'EP1234567A1\nDE 10 2010 054 526 A1\nWO2024123456A1'
                          : '1234567\n7654321'
                      }
                    />

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="font-medium text-sm text-red-900 mb-2">
                          Validation Errors:
                        </div>
                        <ul className="list-disc list-inside space-y-1 text-xs text-red-700">
                          {validationErrors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Format Help */}
                    <div className="mt-2 text-xs text-gray-600">
                      {selectedType === 'publication' &&
                        'Expected format: 2DDDNNNNNN (e.g., 20240123456)'}
                      {selectedType === 'patent' &&
                        'Expected format: 7 or 8 digits (e.g., 9424461, 12345678)'}
                      {selectedType === 'application' &&
                        'Expected format: 8 digits (e.g., 12345678)'}
                      {selectedType === 'foreign_patent' &&
                        'Expected format: country code + number + kind code (e.g., EP1234567A1, DE102010054526A1, CN112345678A)'}
                      <div className="mt-1">One entry per line; spaces within a line are ignored.</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Resource Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Select Document Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                {RESOURCE_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => {
                        // Block foreign_patent and upload for free users (Pro/Premium required)
                        if ((type.id === 'foreign_patent' || type.id === 'upload') && !isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: `${type.id === 'foreign_patent' ? 'Foreign patent onboarding' : 'File uploads'} ${type.id === 'foreign_patent' ? 'is' : 'are'} available for Pro and Premium subscribers. Please upgrade to access this feature.`,
                            duration: 6000,
                          });
                          return;
                        }

                        // Block draft for non-Premium users (Premium-only)
                        if (type.id === 'draft' && !isPremium) {
                          addNotification({
                            type: 'warning',
                            title: 'Premium Required',
                            message: 'Draft documents are available for Premium subscribers only. Please upgrade to Premium to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }

                        if (type.id === 'upload') {
                          if (onOpenUploadModal) {
                            onOpenUploadModal();
                          }
                        } else {
                          setSelectedType(type.id);
                          setDocumentIds('');
                          setValidationErrors([]);
                          setSelectedApplication(null);
                          setProsecutionDocuments([]);
                          setDocumentSearchQuery('');
                          setShowManualEntry(false);
                        }
                      }}
                      disabled={
                        ((type.id === 'foreign_patent' || type.id === 'upload') && !isPaid) ||
                        (type.id === 'draft' && !isPremium)
                      }
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                        (((type.id === 'foreign_patent' || type.id === 'upload') && !isPaid) ||
                        (type.id === 'draft' && !isPremium))
                          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          : selectedType === type.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          (((type.id === 'foreign_patent' || type.id === 'upload') && !isPaid) ||
                          (type.id === 'draft' && !isPremium))
                            ? 'text-gray-300'
                            : selectedType === type.id
                            ? 'text-blue-600'
                            : 'text-gray-400'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div
                            className={`font-medium text-sm ${
                              (((type.id === 'foreign_patent' || type.id === 'upload') && !isPaid) ||
                              (type.id === 'draft' && !isPremium))
                                ? 'text-gray-400'
                                : selectedType === type.id
                                ? 'text-blue-900'
                                : 'text-gray-900'
                            }`}
                          >
                            {type.label}
                          </div>
                          {(type.id === 'foreign_patent' || type.id === 'upload') && !isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                          {type.id === 'draft' && !isPremium && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-0.5 ${
                          (((type.id === 'foreign_patent' || type.id === 'upload') && !isPaid) ||
                          (type.id === 'draft' && !isPremium))
                            ? 'text-gray-400'
                            : 'text-gray-600'
                        }`}>
                          {type.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {selectedType !== 'collection' && (() => {
            // In list mode the button says what it will actually add, so it is clear the
            // search text is the thing being onboarded and not just a filter.
            const typed = documentSearchQuery.trim();
            const inListMode = LIBRARY_TYPES.includes(selectedType) && !showManualEntry;
            const label =
              inListMode && selectedType !== 'draft' && typed
                ? `Add "${typed}"`
                : 'Add Documents';
            return (
              <Button onClick={handleAdd} disabled={!selectedType || isSubmitting}>
                {isSubmitting ? 'Adding...' : label}
              </Button>
            );
          })()}
        </div>
        </div>
      </div>

      {showActiveDocsInfoModal && (
        <div
          className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowActiveDocsInfoModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6 space-y-4 text-sm text-gray-600 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3"
              onClick={() => setShowActiveDocsInfoModal(false)}
            >
              <X className="w-4 h-4" />
            </Button>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Session documents & Usage</h3>
              <p className="text-gray-600 mt-1">
                Anything you add here becomes part of the Active Document list for this session. Chat, tasks, and exports will all
                pull from these materials.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Why they matter</h4>
              <p>
                When you add a patent, application, publication, or upload, it becomes immediately available for citations and
                grounding. Keep only the docs you need so responses stay focused.
              </p>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">Batch mode for multiple documents</h4>
              <p className="mb-2">
                Need to add many documents at once? Click the "Batch Mode" button to switch to batch onboarding.
              </p>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li>Enter multiple document numbers, one per line</li>
                <li>Processes 3 documents at a time automatically</li>
                <li>Shows real-time progress and status for each document</li>
                <li>Perfect for onboarding large portfolios or prior art sets</li>
              </ul>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">Daily document allotment</h4>
              <p>
                Each new document you pull in—manual entry or upload—counts toward your daily allowance. Check your remaining usage
                from the profile menu or by clicking the credits indicator in the top-right corner.
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Tip: remove documents you no longer need to stay within limits and keep your workspace tidy.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
